import React, { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'

import { H4, Flex } from '../../lib/components'
import { EngagementEvents, logCrmEvent } from '../../lib/utils/crmEvents'
import { RootState } from '../../store'
import { Link } from 'react-router-dom'
import { kycServices } from '../../services'
import './investment-details.scss'
import { LegalAgreementProps } from '../../lib/types'

const LegalAgreement = ({
  acceptTOBCheckbox,
  setAcceptTOBCheckbox,
  opportunity,
  pageHeaderText,
  variant = 'default',
  isSubmitting = false,
}: LegalAgreementProps) => {
  const isInvestV2 = variant === 'invest-v2'
  const { authToken, userId } = useSelector((state: RootState) => state.investor)
  const tobPollIntervalRef = useRef<number | null>(null)

  const handleCheckboxClick = async () => {
    const data = await kycServices.acceptTermsOfBusinessAgreement(
      { declaration: true },
      {
        Authorization: authToken ? authToken : '',
      },
    )

    const statusOk =
      data &&
      typeof data === 'object' &&
      'status' in data &&
      (data as { status: number }).status === 200

    if (statusOk) {
      logCrmEvent(
        EngagementEvents.ACCEPT_TERMS_OF_BUSINESS,
        {
          Authorization: authToken ? authToken : '',
        },
        {
          opportunityName: opportunity?.projectName,
          opportunityId: opportunity?.id,
        },
      )
      setAcceptTOBCheckbox(true)
    }

    const kyc = await kycServices.getKYC_v1(userId.toString(), {
      Authorization: authToken ? authToken : '',
    })
    if (kyc?.data?.accept_tob) {
      setAcceptTOBCheckbox(true)
    }
  }

  const handleTobCheckboxChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      await handleCheckboxClick()
    } else {
      setAcceptTOBCheckbox(false)
    }
  }

  useEffect(() => {
    if (tobPollIntervalRef.current) {
      clearInterval(tobPollIntervalRef.current)
      tobPollIntervalRef.current = null
    }

    // invest-v2: user toggles TOB explicitly — polling would fight uncheck/re-check
    if (isInvestV2) return

    if (!acceptTOBCheckbox && authToken && userId) {
      tobPollIntervalRef.current = window.setInterval(async () => {
        try {
          const response = await kycServices.getKYC_v1(userId.toString(), {
            Authorization: authToken,
          })
          if (response?.data?.accept_tob) {
            setAcceptTOBCheckbox(true)
            if (tobPollIntervalRef.current) {
              clearInterval(tobPollIntervalRef.current)
              tobPollIntervalRef.current = null
            }
          }
        } catch (error) {
          console.error('Error polling Terms of Business status:', error)
        }
      }, 3000)
    }

    return () => {
      if (tobPollIntervalRef.current) {
        clearInterval(tobPollIntervalRef.current)
        tobPollIntervalRef.current = null
      }
    }
  }, [acceptTOBCheckbox, authToken, userId, isInvestV2, setAcceptTOBCheckbox])

  const getPageHeaderText = () => {
    if (pageHeaderText) return pageHeaderText
    if (acceptTOBCheckbox) {
      return 'You have accepted the Terms of Business. Please continue.'
    }
    return 'To proceed, please accept our Terms of Business and Term Sheet Agreement.'
  }

  return (
    <>
      <div className='legal-agreement-wrapper'>
        <H4 text={getPageHeaderText()} classes={['']} />

        <Flex classes={['mt-24']}>
          <input
            className='accept-tob'
            type='checkbox'
            checked={acceptTOBCheckbox}
            onChange={
              isInvestV2
                ? handleTobCheckboxChange
                : (e) => {
                    if (e.target.checked) void handleCheckboxClick()
                  }
            }
            disabled={isInvestV2 ? isSubmitting : acceptTOBCheckbox}
            data-testid='accept-tob'
          />
          <span className='basic ml-8'>
            I confirm that I have read and accepted the{' '}
            <Link className='accept-tob-link' to={'/terms-of-business'} target='_blank'>
              Terms of Business
            </Link>
            .
          </span>
        </Flex>
      </div>
    </>
  )
}

export { LegalAgreement }
